// src/components/shared/ProfileModal.jsx
import React, { useState, useEffect } from 'react';
import { 
  X, User, Building, School, BookOpen, Save, Loader, 
  Key, Sparkles, Shield, Settings, CheckCircle, AlertCircle,
  Camera, Mail, Globe
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { UserService } from '../../services/userService';
import { firestoreService } from '../../services/firestoreService';

export const ProfileModal = ({ UserService, onClose, onSave }) => {
  const { user, userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [testingApi, setTestingApi] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  
  const [formData, setFormData] = useState({
    displayName: '',
    photoURL: '',
    bio: '',
    location: '',
    website: '',
    apiKeys: {
      gemini: ''
    },
    affiliation: {
      type: '',
      name: '',
      role: '',
      district: '',
      circuit: '',
      school: '',
      subjects: [],
      grades: [],
      institutionType: ''
    }
  });

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'api', label: 'API Keys', icon: Key },
    { id: 'affiliation', label: 'Affiliation', icon: Building }
  ];

  const loadUserData = async () => {
    setLoading(true);
    try {
      const apiKeys = await UserService.getUserApiKeys(user.uid);
      
      setFormData({
        displayName: userProfile?.displayName || user.displayName || '',
        photoURL: user.photoURL || '',
        bio: userProfile?.bio || '',
        location: userProfile?.location || '',
        website: userProfile?.website || '',
        apiKeys: apiKeys || { gemini: '' },
        affiliation: userProfile?.affiliation || {
          type: '',
          name: '',
          role: '',
          district: '',
          circuit: '',
          school: '',
          subjects: [],
          grades: [],
          institutionType: ''
        }
      });
      
      setPhotoPreview(user.photoURL || '');
    } catch (error) {
      console.error('Error loading user data:', error);
      setError('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image must be less than 5MB');
        return;
      }
      
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const keys = field.split('.');
      setFormData(prev => {
        const newData = { ...prev };
        let current = newData;
        for (let i = 0; i < keys.length - 1; i++) {
          current[keys[i]] = { ...current[keys[i]] };
          current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
        return newData;
      });
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSubjectsChange = (subject) => {
    setFormData(prev => ({
      ...prev,
      affiliation: {
        ...prev.affiliation,
        subjects: prev.affiliation.subjects.includes(subject)
          ? prev.affiliation.subjects.filter(s => s !== subject)
          : [...prev.affiliation.subjects, subject]
      }
    }));
  };

  const handleGradesChange = (grade) => {
    setFormData(prev => ({
      ...prev,
      affiliation: {
        ...prev.affiliation,
        grades: prev.affiliation.grades.includes(grade)
          ? prev.affiliation.grades.filter(g => g !== grade)
          : [...prev.affiliation.grades, grade]
      }
    }));
  };

  const testGeminiApiKey = async () => {
    if (!formData.apiKeys.gemini) {
      setError('Please enter an API key first');
      return;
    }

    setTestingApi(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(
        'https://generativelanguage.googleapis.com/v1beta/models/gemini-pro:generateContent?key=' + formData.apiKeys.gemini,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contents: [{ parts: [{ text: 'Hello' }] }]
          })
        }
      );

      if (response.ok) {
        setSuccess('✅ API Key is valid!');
      } else {
        setError('❌ API Key test failed');
      }
    } catch (error) {
      setError('❌ Connection failed');
    } finally {
      setTestingApi(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      let photoURL = formData.photoURL;

      // Upload photo if changed
      if (photoFile) {
        const uploadResult = await firestoreService.uploadFile(
          photoFile,
          `users/${user.uid}/profile`,
          'photo'
        );
        photoURL = uploadResult.url;
      }

      // Save profile data
      const updated = await UserService.updateUserProfile(user.uid, {
        ...formData,
        photoURL
      });

      // Save API keys separately
      if (formData.apiKeys.gemini) {
        await UserService.saveUserApiKeys(user.uid, formData.apiKeys);
      }

      setSuccess('Profile updated successfully!');
      if (onSave) onSave(updated);
      
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error saving profile:', error);
      setError('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const needsEducationalFields = () => {
    const { type, role, institutionType } = formData.affiliation;
    
    // For institutions
    if (type === 'institution') {
      if (institutionType === 'school') {
        return role === 'learner' || role === 'educator';
      }
      if (institutionType === 'college' || institutionType === 'university') {
        return role === 'student' || role === 'lecturer';
      }
    }
    
    // For projects
    if (type === 'project') {
      return role === 'learner' || role === 'educator';
    }
    
    return false;
  };

  const SUBJECTS = [
    'Mathematics', 'Physical Sciences', 'Life Sciences', 'English', 
    'Afrikaans', 'IsiZulu', 'History', 'Geography', 'Accounting',
    'Business Studies', 'Economics', 'Computer Applications Technology',
    'Information Technology', 'Engineering Graphics and Design',
    'Visual Arts', 'Music', 'Dramatic Arts', 'Consumer Studies',
    'Hospitality Studies', 'Tourism', 'Agricultural Sciences'
  ];

  const GRADES = ['R', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

  const DISTRICTS = [
    'Amajuba', 'eThekwini', 'iLembe', 'Pinetown', 'Ugu', 'uMgungundlovu',
    'uMkhanyakude', 'uMzinyathi', 'uThungulu', 'Zululand', 'Harry Gwala'
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Profile Settings</h2>
            <p className="text-sm text-gray-600 mt-1">Manage your account</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <div className="flex gap-1 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-indigo-600 text-indigo-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
          ) : (
            <>
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div className="space-y-6">
                  {/* Profile Photo */}
                  <div className="flex items-center gap-6">
                    <div className="relative">
                      {photoPreview ? (
                        <img
                          src={photoPreview}
                          alt="Profile"
                          className="h-24 w-24 rounded-full object-cover ring-4 ring-indigo-100"
                        />
                      ) : (
                        <div className="h-24 w-24 rounded-full bg-indigo-100 flex items-center justify-center">
                          <User className="h-12 w-12 text-indigo-600" />
                        </div>
                      )}
                      <label className="absolute bottom-0 right-0 p-2 bg-indigo-600 text-white rounded-full cursor-pointer hover:bg-indigo-700 shadow-lg">
                        <Camera className="h-4 w-4" />
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoSelect}
                          className="hidden"
                        />
                      </label>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Profile Photo</h3>
                      <p className="text-sm text-gray-600 mt-1">JPG, PNG. Max 5MB.</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Display Name</label>
                    <input
                      type="text"
                      value={formData.displayName}
                      onChange={(e) => handleInputChange('displayName', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Bio</label>
                    <textarea
                      value={formData.bio}
                      onChange={(e) => handleInputChange('bio', e.target.value)}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 resize-none"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Location</label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        value={formData.location}
                        onChange={(e) => handleInputChange('location', e.target.value)}
                        placeholder="City, Country"
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Website</label>
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) => handleInputChange('website', e.target.value)}
                      placeholder="https://example.com"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              )}

              {/* API Keys Tab */}
              {activeTab === 'api' && (
                <div className="space-y-6">
                  <div className="border rounded-lg p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-purple-600" />
                        <h4 className="font-medium text-gray-900">Gemini API Key</h4>
                      </div>
                      {formData.apiKeys.gemini && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          Connected
                        </span>
                      )}
                    </div>

                    <input
                      type="password"
                      value={formData.apiKeys.gemini}
                      onChange={(e) => handleInputChange('apiKeys.gemini', e.target.value)}
                      placeholder="AIza..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 font-mono text-sm"
                    />
                    
                    {formData.apiKeys.gemini && (
                      <button
                        onClick={testGeminiApiKey}
                        disabled={testingApi}
                        className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {testingApi ? (
                          <>
                            <Loader className="h-4 w-4 animate-spin" />
                            Testing...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="h-4 w-4" />
                            Test Connection
                          </>
                        )}
                      </button>
                    )}

                    <details className="text-sm">
                      <summary className="cursor-pointer text-purple-600 hover:text-purple-800 font-medium py-2">
                        How to get API key
                      </summary>
                      <div className="mt-2 p-4 bg-purple-50 rounded-lg space-y-2 text-gray-700">
                        <p><strong>1.</strong> Visit <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-purple-600 underline">aistudio.google.com</a></p>
                        <p><strong>2.</strong> Sign in with Google</p>
                        <p><strong>3.</strong> Create API Key</p>
                        <p><strong>4.</strong> Paste above</p>
                      </div>
                    </details>
                  </div>

                  <div className="flex gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-900">
                      <p className="font-medium">Your keys are secure</p>
                      <p className="text-xs text-blue-700 mt-1">
                        API keys are encrypted and only accessible by you
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Affiliation Tab - Keep existing logic */}
              {activeTab === 'affiliation' && (
                <div className="space-y-6">
                 {/* Affiliation */}
                <div className="space-y-4 border-t pt-6">
                  <h3 className="text-lg font-semibold text-gray-900">Affiliation</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Affiliation Type
                    </label>
                    <select
                      value={formData.affiliation.type}
                      onChange={(e) => handleInputChange('affiliation.type', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">Select type...</option>
                      <option value="organisation">Organisation</option>
                      <option value="institution">Institution (School/College/University)</option>
                      <option value="project">Educational Project</option>
                    </select>
                  </div>

                  {formData.affiliation.type && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {formData.affiliation.type === 'organisation' && 'Organisation Name'}
                          {formData.affiliation.type === 'institution' && 'Institution Name'}
                          {formData.affiliation.type === 'project' && 'Project Name'}
                        </label>
                        <input
                          type="text"
                          value={formData.affiliation.name}
                          onChange={(e) => handleInputChange('affiliation.name', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="Enter name..."
                        />
                      </div>

                      {formData.affiliation.type === 'institution' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Institution Type
                          </label>
                          <select
                            value={formData.affiliation.institutionType}
                            onChange={(e) => handleInputChange('affiliation.institutionType', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          >
                            <option value="">Select type...</option>
                            <option value="school">School</option>
                            <option value="college">College</option>
                            <option value="university">University</option>
                          </select>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Your Role
                        </label>
                        <select
                          value={formData.affiliation.role}
                          onChange={(e) => handleInputChange('affiliation.role', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="">Select role...</option>
                          {formData.affiliation.type === 'organisation' && (
                            <>
                              <option value="employee">Employee</option>
                              <option value="manager">Manager</option>
                              <option value="director">Director</option>
                              <option value="consultant">Consultant</option>
                              <option value="volunteer">Volunteer</option>
                            </>
                          )}
                          {formData.affiliation.type === 'institution' && formData.affiliation.institutionType === 'school' && (
                            <>
                              <option value="learner">Learner</option>
                              <option value="educator">Educator</option>
                            </>
                          )}
                          {formData.affiliation.type === 'institution' && (formData.affiliation.institutionType === 'college' || formData.affiliation.institutionType === 'university') && (
                            <>
                              <option value="student">Student</option>
                              <option value="lecturer">Lecturer</option>
                            </>
                          )}
                          {formData.affiliation.type === 'project' && (
                            <>
                              <option value="learner">Learner</option>
                              <option value="educator">Educator</option>
                            </>
                          )}
                        </select>
                      </div>
                    </>
                  )}

                  {/* Educational Fields for Learners/Educators/Students/Lecturers */}
                  {needsEducationalFields() && (
                    <div className="space-y-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                        <School className="h-5 w-5" />
                        Educational Details
                      </h4>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          District
                        </label>
                        <select
                          value={formData.affiliation.district}
                          onChange={(e) => handleInputChange('affiliation.district', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Select district...</option>
                          {DISTRICTS.map(district => (
                            <option key={district} value={district}>{district}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Circuit
                        </label>
                        <input
                          type="text"
                          value={formData.affiliation.circuit}
                          onChange={(e) => handleInputChange('affiliation.circuit', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter circuit name..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          School
                        </label>
                        <input
                          type="text"
                          value={formData.affiliation.school}
                          onChange={(e) => handleInputChange('affiliation.school', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter school name..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Subjects
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-3 bg-white">
                          {SUBJECTS.map(subject => (
                            <label key={subject} className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 p-1 rounded">
                              <input
                                type="checkbox"
                                checked={formData.affiliation.subjects.includes(subject)}
                                onChange={() => handleSubjectsChange(subject)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">{subject}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Grade(s)
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {GRADES.map(grade => (
                            <label key={grade} className="cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.affiliation.grades.includes(grade)}
                                onChange={() => handleGradesChange(grade)}
                                className="sr-only peer"
                              />
                              <div className="px-4 py-2 border-2 border-gray-300 rounded-lg peer-checked:border-blue-500 peer-checked:bg-blue-50 peer-checked:text-blue-700 hover:bg-gray-50 transition-colors">
                                Grade {grade}
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-900">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}
          
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-sm text-green-900">
              <CheckCircle className="h-4 w-4 flex-shrink-0" />
              {success}
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};